import React, { useState, useRef, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import Cookies from 'js-cookie';
import ReactLoading from 'react-loading';
import InfiniteScroll from 'react-infinite-scroller';
import Tooltip from '@mui/material/Tooltip';
import api from 'api';
import { FormControlLabel, Checkbox } from '@material-ui/core';
import { Header, SideMenu, Search, Button, Input, Dialog, Select } from 'components';
import { Container, ContentArea, Title, Div, DataArea, LoadingArea, CountArea, ModalButton } from './styles';
import { loadingColor, primary, secondary, white } from 'styles/colorProvider';
import { TiFilter } from 'react-icons/ti';
import { toast } from 'react-toastify';
import { dataColumns } from './helper';
import { RiFileExcel2Fill } from 'react-icons/ri';
import { partnersInfo } from 'utils/general';
import moment from 'moment';
import DateFnsUtils from '@date-io/date-fns';
import { DatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { ptBR } from 'date-fns/locale';

const MAX_DATE_RANGE_DAYS = 31;

const validateDateRange = (startStr, endStr) => {
  if (!startStr || !endStr || startStr.length < 10 || endStr.length < 10) return { valid: true };
  const start = moment(startStr, 'DD/MM/YYYY', true);
  const end = moment(endStr, 'DD/MM/YYYY', true);
  if (!start.isValid()) return { valid: false, error: 'Data inicial inválida. Use DD/MM/AAAA.' };
  if (!end.isValid()) return { valid: false, error: 'Data final inválida. Use DD/MM/AAAA.' };
  if (end.isBefore(start)) return { valid: false, error: 'A data final deve ser igual ou posterior à data inicial.' };
  if (end.diff(start, 'days') > MAX_DATE_RANGE_DAYS) {
    return { valid: false, error: `O intervalo não pode ser maior que ${MAX_DATE_RANGE_DAYS} dias (1 mês).` };
  }
  return { valid: true };
};

function Policies({ history }) {
  const scrollParent = useRef();

  const [loading, setLoading] = useState(false);
  const [policiesList, setPoliciesList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalCount, setTotalCount] = useState();
  const [hasNextPage, setHasNextPage] = useState();
  const [search, setSearch] = useState();
  const [open, setOpen] = useState(false);
  const [selectStatus, setSelectStatus] = useState([]);
  const [startData, setStartdata] = useState('');
  const [endData, setEndData] = useState('');
  const [touchFilter, setTouchFilter] = useState();
  const [loadingExport, setLoadingExport] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState([]);
  const [dateRangeError, setDateRangeError] = useState('');
  const [dateField, setDateField] = useState('created_at');

  const loadData = async (overrides = {}) => {
    const partner = overrides.partner !== undefined ? overrides.partner : selectedPartner;
    const status = overrides.status !== undefined ? overrides.status : selectStatus;
    const start = overrides.startdate !== undefined ? overrides.startdate : startData;
    const end = overrides.enddate !== undefined ? overrides.enddate : endData;
    const page = overrides.page !== undefined ? overrides.page : currentPage;
    const searchParam = overrides.search !== undefined ? overrides.search : search;
    const dateFieldParam = overrides.datefield !== undefined ? overrides.datefield : dateField;

    setLoading(true);
    const resp = await api({
      method: 'GET',
      url: `/splitrisk-policies`,
      headers: {
        'Content-Type': 'application/json',
        Authorization: Cookies.get('splitrisk-authtoken'),
      },
      params: {
        search: searchParam,
        startdate: start === '' ? undefined : start,
        enddate: end === '' ? undefined : end,
        status: Array.isArray(status) ? (status.length ? status.join(',') : undefined) : status,
        page,
        take: 20,
        partner: Array.isArray(partner) ? (partner.length ? partner.join(',') : undefined) : partner,
        datefield: dateFieldParam,
      },
      json: true,
    });

    setPoliciesList(resp?.data?.policies);
    setTotalCount(resp?.data?.totalCount);
    setHasNextPage(resp?.data?.hasNextPage);
    setLoading(false);
    setTouchFilter(false);
    if (overrides.page !== undefined) setCurrentPage(overrides.page);
  };

  const handleLoadMore = async () => {
    if (!touchFilter) {
      const page = currentPage + 1;
      if (hasNextPage && !loadingMore) {
        setLoadingMore(true);

        const resp = await api({
          method: 'GET',
          url: `/splitrisk-policies`,
          headers: {
            'Content-Type': 'application/json',
            Authorization: Cookies.get('splitrisk-authtoken'),
          },
          params: {
            search,
            startdate: startData === '' ? undefined : startData,
            enddate: endData === '' ? undefined : endData,
            status: selectStatus.length ? selectStatus.join(',') : undefined,
            page,
            take: 10,
            partner: selectedPartner.length ? selectedPartner.join(',') : undefined,
            datefield: dateField,
          },
          json: true,
        });

        const data = policiesList;
        const ndata = data.concat(resp?.data?.policies);

        setCurrentPage(page);
        setPoliciesList(ndata);
        setHasNextPage(resp?.data?.hasNextPage);
        setLoadingMore(false);
      }
    }
  };

  const handleClear = () => {
    setSelectedPartner([]);
    setSelectStatus([]);
    setStartdata('');
    setEndData('');
    setDateRangeError('');
    setDateField('created_at');
    setCurrentPage(1);
    setTouchFilter(true);
    loadData({ partner: [], status: [], startdate: '', enddate: '', page: 1, datefield: 'created_at' });
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenFilter = () => {
    setDateRangeError('');
    setOpen(true);
  };

  const handleApplyFilter = () => {
    const { valid, error } = validateDateRange(startData, endData);
    if (!valid) {
      setDateRangeError(error);
      toast.error(error, { position: toast.POSITION.TOP_RIGHT, theme: 'colored' });
      return;
    }
    setDateRangeError('');
    setTouchFilter(true);
    loadData();
    setOpen(false);
  };

  const handleDateChange = (field, value) => {
    setDateRangeError('');
    if (field === 'start') setStartdata(value);
    else setEndData(value);
  };

  const parseDateForPicker = (str) => {
    if (!str || String(str).trim().length < 10) return null;
    const m = moment(str, 'DD/MM/YYYY', true);
    return m.isValid() ? m.toDate() : null;
  };

  const partnerOptions = Array.from(
    new Map((partnersInfo || []).map((p) => [p.id_partner, p]).filter(([, p]) => p?.id_partner != null)).values(),
  ).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const statusOptions = [
    { value: 'active', label: 'Ativo' },
    { value: 'canceled', label: 'Cancelado' },
    { value: 'awaiting_cancellation', label: 'Aguardando cancelamento' },
    { value: 'expired', label: 'Expirado' },
    { value: 'awaiting_reactive', label: 'Aguardando reativação' },
  ];

  const togglePartner = (id) => {
    setSelectedPartner((prev) => (prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]));
  };

  const toggleStatus = (value) => {
    setSelectStatus((prev) => (prev.includes(value) ? prev.filter((s) => s !== value) : [...prev, value]));
  };

  const renderFilterDialog = () => (
    <Dialog open={open} onClose={() => setOpen(false)} width="50%" title="Filtrar contrato">
      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Canal (MGA)</label>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectedPartner.length === 0}
              indeterminate={selectedPartner.length > 0 && selectedPartner.length < partnerOptions.length}
              onChange={() => setSelectedPartner([])}
            />
          }
          label="Todos os canais"
        />
        <div style={{ maxHeight: 180, overflowY: 'auto', marginLeft: 8 }}>
          {partnerOptions.map((partner) => (
            <FormControlLabel
              key={partner.id_partner}
              control={
                <Checkbox
                  checked={selectedPartner.includes(partner.id_partner)}
                  onChange={() => togglePartner(partner.id_partner)}
                />
              }
              label={partner.name}
            />
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Status da apólice</label>
        <FormControlLabel
          control={
            <Checkbox
              checked={selectStatus.length === 0}
              indeterminate={selectStatus.length > 0 && selectStatus.length < statusOptions.length}
              onChange={() => setSelectStatus([])}
            />
          }
          label="Todos os status"
        />
        <div style={{ marginLeft: 8 }}>
          {statusOptions.map((opt) => (
            <FormControlLabel
              key={opt.value}
              control={<Checkbox checked={selectStatus.includes(opt.value)} onChange={() => toggleStatus(opt.value)} />}
              label={opt.label}
            />
          ))}
        </div>
      </div>

      <Select height="30px" label="Filtrar datas por" value={dateField} onChange={(e) => setDateField(e.target.value)}>
        <option value="created_at">Data de criação da apólice</option>
        <option value="expiration_date">Data de expiração da apólice</option>
      </Select>

      <div style={{ marginTop: 8 }}>
        <MuiPickersUtilsProvider utils={DateFnsUtils} locale={ptBR}>
          <div style={{ marginBottom: 16 }}>
            <DatePicker
              label={dateField === 'expiration_date' ? 'Data inicial (expiração)' : 'Data inicial (criação)'}
              value={parseDateForPicker(startData)}
              onChange={(date) => handleDateChange('start', date ? moment(date).format('DD/MM/YYYY') : '')}
              format="dd/MM/yyyy"
              emptyLabel="DD/MM/AAAA"
              invalidDateMessage="Data inválida"
              inputVariant="outlined"
              fullWidth
              cancelLabel="Cancelar"
              okLabel="OK"
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <DatePicker
              label={dateField === 'expiration_date' ? 'Data final (expiração)' : 'Data final (criação)'}
              value={parseDateForPicker(endData)}
              onChange={(date) => handleDateChange('end', date ? moment(date).format('DD/MM/YYYY') : '')}
              format="dd/MM/yyyy"
              emptyLabel="DD/MM/AAAA"
              invalidDateMessage="Data inválida"
              inputVariant="outlined"
              fullWidth
              cancelLabel="Cancelar"
              okLabel="OK"
            />
          </div>
        </MuiPickersUtilsProvider>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: '#666' }}>
          Intervalo máximo: 1 mês ({MAX_DATE_RANGE_DAYS} dias). Preencha as duas datas para filtrar por período.
        </p>
        {dateRangeError && <p style={{ margin: '4px 0 0', fontSize: 12, color: '#d32f2f' }}>{dateRangeError}</p>}
      </div>

      <ModalButton>
        <Button
          height="35px"
          outlined
          disabled={
            selectedPartner.length === 0 && selectStatus.length === 0 && !startData && !endData && dateField === 'created_at'
          }
          onClick={handleClear}
        >
          Limpar filtros
        </Button>
        <Button height="35px" onClick={handleApplyFilter}>
          Aplicar alterações
        </Button>
      </ModalButton>
    </Dialog>
  );

  const handleExport = async () => {
    const { valid, error: dateError } = validateDateRange(startData, endData);
    if (!valid) {
      toast.error(dateError, { position: toast.POSITION.TOP_RIGHT, theme: 'colored' });
      return;
    }

    setLoadingExport(true);

    const params = new URLSearchParams();
    if (selectedPartner.length) params.set('partner', selectedPartner.join(','));
    if (selectStatus.length) params.set('status', selectStatus.join(','));
    if (startData && startData.length >= 10) params.set('startdate', startData);
    if (endData && endData.length >= 10) params.set('enddate', endData);
    params.set('datefield', dateField);
    const queryString = params.toString() ? `?${params.toString()}` : '';

    try {
      const resp = await api({
        method: 'GET',
        url: `/policies-report/stream${queryString}`,
        headers: {
          Authorization: Cookies.get('splitrisk-authtoken'),
          feature: 'policies-report',
        },
        responseType: 'blob',
      });

      const blob = resp?.data;
      if (blob && blob.size > 0) {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `contratos_${new Date().toISOString().split('T')[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success('Download iniciado!', {
          position: toast.POSITION.TOP_RIGHT,
          theme: 'colored',
        });
      } else {
        toast.info('Não há dados para exportar', {
          position: toast.POSITION.TOP_RIGHT,
          theme: 'colored',
        });
      }

      setLoadingExport(false);
    } catch (err) {
      let message = 'Não foi possível exportar contratos';
      const status = err?.response?.status;
      const data = err?.response?.data;
      if (status === 400 && data instanceof Blob) {
        try {
          const text = await data.text();
          const j = JSON.parse(text);
          if (j?.error) message = j.error;
        } catch (_) {}
      } else if (data?.error) message = data.error;
      else if (typeof data?.message === 'string') message = data.message;
      toast.error(message, {
        position: toast.POSITION.TOP_RIGHT,
        theme: 'colored',
      });
      setLoadingExport(false);
    }
  };

  return (
    <>
      <Header />
      <Container>
        {renderFilterDialog()}
        <SideMenu />

        <ContentArea>
          <Div>
            <Title>CONTRATOS</Title>
            <div className="containerTitle">
              <Tooltip title="Relatório de contratos">
                <Button height="30px" width="200px" onClick={handleExport} disabled={loadingExport}>
                  {loadingExport ? (
                    <ReactLoading color={white} height={25} width={25} type="spin" />
                  ) : (
                    <RiFileExcel2Fill size={18} />
                  )}
                </Button>
              </Tooltip>
              <Button title="Filtrar" height="30px" onClick={handleOpenFilter}>
                <TiFilter size={18} />
              </Button>
              <Search
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onClear={() => {
                  setSearch('');
                  loadData();
                }}
                onConfirm={() => loadData()}
              />
            </div>
          </Div>

          <DataArea ref={scrollParent}>
            <InfiniteScroll
              loadMore={handleLoadMore}
              hasMore={hasNextPage}
              useWindow={false}
              getScrollParent={() => scrollParent?.current}
              initialLoad={false}
              style={{ width: '100%', display: 'flex' }}
              threshold={10}
            >
              <DataTable
                onRowClicked={(row) => history.push(`/backoffice/detalhes-do-contrato/${row?.id}`)}
                persistTableHead
                responsive
                columns={dataColumns}
                data={policiesList}
                highlightOnHover
                pointerOnHover
                fixedHeader
                fixedHeaderScrollHeight="100%"
                progressPending={loading}
                progressComponent={
                  <LoadingArea>
                    <ReactLoading color={loadingColor} height={40} width={40} type="spin" />
                  </LoadingArea>
                }
                noDataComponent={<small style={{ marginTop: 20 }}>Nenhum contrato encontrado</small>}
              />
            </InfiniteScroll>
          </DataArea>

          <CountArea>
            {loadingMore && <ReactLoading color={loadingColor} height={20} width={20} type="spin" />}
            {!loadingMore && !loading && (
              <p style={{ color: primary }}>
                Mostrando {policiesList?.length} de {totalCount}
              </p>
            )}
          </CountArea>
        </ContentArea>
      </Container>
    </>
  );
}

export default Policies;
